import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Select } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import { getAccessTokenOrRedirect } from '../../utils/auth';
import { useAuth } from '../../providers/AuthProvider';
import { API_URL } from '../../env';
import { Search } from 'lucide-react';

interface User {
  staffid: number;
  firstname: string;
  lastname: string;
  alias_name: string | null;
}

interface UserResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: any[]; // API response users
}

interface UserDropdownProps {
  onUserChange: (staffid: number | null) => void;
  className?: string;
  isAdmin?: boolean;
  isTeamLead?: boolean;
  currentUserId?: string;
}

const UserDropdown: React.FC<UserDropdownProps> = ({
  onUserChange,
  className = '',
  isAdmin = false,
  isTeamLead = false,
  currentUserId = ''
}) => {
  const { userInfo } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout>();

  // Fetch users from API
  const fetchUsers = useCallback(async (page: number = 1, search?: string) => {
    try {
      if (page > 1) {
        setFetchingMore(true);
      } else if (search !== undefined) {
        setSearching(true);
      }

      const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));

      const headers = {
        'Authorization': `Bearer ${jwt}`
      };

      // Build the API URL based on user role and search parameters
      let apiUrl = `${API_URL}/api/user-subordinates/?page=${page}&per_page=10&status=1`;

      // Add search parameter if provided
      if (search) {
        apiUrl += `&search=${encodeURIComponent(search)}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user subordinates');
      }

      const data: UserResponse = await response.json();

      // Transform the data to match the expected format
      const transformedUsers = data.results.map(user => ({
        staffid: user.id,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        alias_name: user.alias_name
      }));

      if (page === 1) {
        setUsers(transformedUsers);
      } else {
        setUsers(prev => [...prev, ...transformedUsers]);
      }

      setHasMore(!!data.next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setFetchingMore(false);
      setSearching(false);
    }
  }, [isAdmin, isTeamLead, currentUserId, userInfo]);

  // Initial fetch
  useEffect(() => {
    // Only fetch users when the dropdown is first rendered
    // This prevents infinite API calls
    const initialFetch = async () => {
      await fetchUsers(1);
    };

    initialFetch();

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [isAdmin, isTeamLead, currentUserId]); // Only re-run when these props change

  // Debounced search
  const debouncedSearch = useCallback((value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setUsers([]);
      setCurrentPage(1);
      fetchUsers(1, value);
    }, 500);
  }, [fetchUsers]);

  // Load more users when scrolling
  const loadMore = useCallback(() => {
    if (hasMore && !fetchingMore) {
      fetchUsers(currentPage + 1);
    }
  }, [currentPage, hasMore, fetchingMore, fetchUsers]);

  // Convert users to options for Select component
  const options = users.map(user => ({
    value: user.staffid,
    label: `${user.alias_name || user.firstname + ' ' + user.lastname}`,
  }));

  // Handle change in selection
  const handleChange = (value: number | null) => {
    onUserChange(value);
  };

  return (
    <div className={`flex items-center gap-2 p-2 ${className}`}>

      <Select
        showSearch
        allowClear
        style={{
          width: 300,
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}
        placeholder="Search users..."
        optionFilterProp="label"
        onChange={handleChange}
        onClear={() => onUserChange(null)}
        options={options}
        className="ant-select ant-select-outlined bg-white rounded-lg min-h-[40px] !w-full min-w-[300px] shadow-sm"
        suffixIcon={<Search className="h-5 mt-[-1px] text-[#6c5ffc]" />}
        popupClassName="max-h-[300px]"
        dropdownStyle={{ borderRadius: '8px' }}
        filterOption={false}
        onSearch={debouncedSearch}
        onPopupScroll={(e) => {
          const target = e.target as HTMLDivElement;
          if (
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 30 &&
            hasMore &&
            !fetchingMore
          ) {
            loadMore();
          }
        }}
        dropdownRender={(menu) => (
          <div>
            {menu}
            {fetchingMore && (
              <div className="text-center py-2">
                <LoadingOutlined style={{ fontSize: 24 }} spin />
              </div>
            )}
          </div>
        )}
        notFoundContent={
          searching ? (
            <div className="text-center py-2">
              <LoadingOutlined style={{ fontSize: 24 }} spin />
            </div>
          ) : (
            <div className="text-center py-2 text-sm text-gray-500">
              No users found
            </div>
          )
        }
      />
    </div>
  );
};

export default UserDropdown;
